import React, { useState, useEffect } from "react";
//import { RefreshCw } from "lucide-react";
import { FaSyncAlt } from "react-icons/fa";
import { Container, Row, Col, Button, Card, Spinner } from "react-bootstrap";

import VampAnalytics from "./analytics/VampAnalytics";
import OrdersAnalytics from "./analytics/OrdersAnalytics";
import LatencyAnalytics from "./analytics/LatencyAnalytics";
import RiskSummaryAnalytics from "./analytics/RiskSummaryAnalytics";
import ThreeDSAnalytics from "./analytics/ThreeDSAnalytics";
import { BinDistribution } from "./analytics/BinDistributionAnalytics";
import { RuleFrequency } from "./analytics/RuleFrequencyAnalytics";

// const DEFAULT_API_BASE = "http://localhost:8080/analytics";
 const DEFAULT_API_BASE = "https://coral-app-2-4y6qg.ondigitalocean.app/analytics";
const DEFAULT_SUBSCRIBER_ID = 12;
const EXCLUDE_DEV = false;
const REFRESH_INTERVAL = 3600000;

// 👇 Add this:
const SUBSCRIBER_LABEL = "CasinoKai • Fyntek";


// NEW Enterprise KPI Card
const KpiCard = ({ label, value, sublabel, icon, accent }) => (
  <div
    style={{
      background: "rgba(255,255,255,0.65)",
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
      borderRadius: "14px",
      padding: "20px 24px",
      boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
      border: "1px solid rgba(255,255,255,0.4)",
      transition: "transform 0.15s ease, box-shadow 0.15s ease",
      cursor: "default",
    }}
    onMouseOver={(e) => {
      e.currentTarget.style.transform = "translateY(-3px)";
      e.currentTarget.style.boxShadow = "0 6px 24px rgba(0,0,0,0.12)";
    }}
    onMouseOut={(e) => {
      e.currentTarget.style.transform = "translateY(0px)";
      e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.06)";
    }}
  >
    {/* Top row: icon + label */}
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: "10px",
          backgroundColor: accent || "#3B82F6",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          color: "#FFF",
          fontSize: 16,
          fontWeight: 600,
        }}
      >
        {icon}
      </div>
      <span
        style={{
          fontSize: 13,
          color: "#475569",
          fontWeight: 600,
        }}
      >
        {label}
      </span>
    </div>

    {/* Metric number */}
    <div
      style={{
        fontSize: 32,
        fontWeight: 700,
        marginTop: 10,
        marginBottom: 4,
        color: "#0F172A",
      }}
    >
      {value}
    </div>

    {/* Sublabel */}
    {sublabel && <div style={{ fontSize: 12, color: "#64748B" }}>{sublabel}</div>}
  </div>
);

// Convert minutes → nice string (ms / sec / min)
const formatLatencyFromMinutes = (valueInMinutes) => {
  const v0 = Number(valueInMinutes);
  const v = Math.abs(v0);   // ✅ add this
  if (!Number.isFinite(v)) return "-";
  
  
  const ms = v * 60_000; // minutes → ms

  if (ms < 1000) {
    return `${ms.toFixed(0)} ms`;
  }

  const sec = ms / 1000;
  if (sec < 60) {
    return `${sec.toFixed(2)} sec`;
  }

  const min = sec / 60;
  return `${min.toFixed(2)} min`;
};
/*
// Helper: map raw three_ds values into buckets
function bucketThreeDSLevel(raw) {
  if (raw === null || raw === undefined) return "NOT_ATTEMPTED";

  const value = raw.toString().trim();
  if (!value || value === "N/A" || value.toLowerCase() === "none") {
    return "NOT_ATTEMPTED";
  }

  const lower = value.toLowerCase();

  if (lower.includes("friction")) return "FRICTIONLESS";
  if (lower.includes("chall")) return "CHALLENGED"; // challenge, challenged, challenge_required, etc.
  if (lower.includes("fail") || lower.includes("denied") || lower.includes("declin")) return "FAILED";
  if (lower.includes("no_3ds") || lower.includes("no-3ds") || lower.includes("bypass")) return "NOT_ATTEMPTED";
  if (lower === "a" || lower.includes("attempt")) return "NOT_ATTEMPTED";

  return "OTHER";
}*/

// Generic normalizer: handle Grafana-style and object-list responses
function normalizeLevelCountRows(raw, levelFieldCandidates, countFieldCandidates) {
  if (!raw) return [];

  // Case 1: object list — { data: [ { level: "Frictionless", count: 10 }, ... ] }
  if (Array.isArray(raw.data) && raw.data.length > 0 && typeof raw.data[0] === "object") {
    return raw.data.map((entry) => {
      const levelField = levelFieldCandidates.find((k) => k in entry);
      const countField = countFieldCandidates.find((k) => k in entry);
      return {
        level: levelField ? entry[levelField] : null,
        count: Number(countField ? entry[countField] : 0),
      };
    });
  }

  // Case 2: Grafana-style at root — { columns: [...], data: [[...], ...] }
  if (Array.isArray(raw.columns) && Array.isArray(raw.data) && raw.data.length > 0 && Array.isArray(raw.data[0])) {
    const cols = raw.columns;
    const rows = raw.data;
    const levelIdx = levelFieldCandidates.map((k) => cols.indexOf(k)).find((idx) => idx !== -1);
    const countIdx = countFieldCandidates.map((k) => cols.indexOf(k)).find((idx) => idx !== -1);

    if (levelIdx == null || countIdx == null) return [];

    return rows.map((row) => ({
      level: row[levelIdx],
      count: Number(row[countIdx] || 0),
    }));
  }

  // Case 3: Grafana-style nested — { data: { columns: [...], data: [[...], ...] } }
  if (
    raw.data &&
    Array.isArray(raw.data.columns) &&
    Array.isArray(raw.data.data) &&
    raw.data.data.length > 0 &&
    Array.isArray(raw.data.data[0])
  ) {
    const cols = raw.data.columns;
    const rows = raw.data.data;
    const levelIdx = levelFieldCandidates.map((k) => cols.indexOf(k)).find((idx) => idx !== -1);
    const countIdx = countFieldCandidates.map((k) => cols.indexOf(k)).find((idx) => idx !== -1);

    if (levelIdx == null || countIdx == null) return [];

    return rows.map((row) => ({
      level: row[levelIdx],
      count: Number(row[countIdx] || 0),
    }));
  }

  return [];
}

export default function AnalyticsDashboard({ apiBase = DEFAULT_API_BASE, subscriberId = DEFAULT_SUBSCRIBER_ID, subscriberLabel = SUBSCRIBER_LABEL } = {}) {
  const [vampData, setVampData] = useState(null);
  const [ordersData, setOrdersData] = useState(null);
  const [latencyData, setLatencyData] = useState(null);
  const [riskSummaryData, setRiskSummaryData] = useState([]); // default to []
  const [threeDSData, setThreeDSData] = useState([]); // default to []

  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const [timeRange, setTimeRange] = useState("24h");
  const [environment, setEnvironment] = useState("All");

  const [binData, setBinData] = useState([]);
  const [ruleData, setRuleData] = useState([]);

  const [timingTotalStats, setTimingTotalStats] = useState(null);
  const [timingEvaluateStats, setTimingEvaluateStats] = useState(null);
  /*
  const fetchJSON = async (url) => {
    try {
      const response = await fetch(url);
      return await response.json();
    } catch (err) {
      console.error(`Fetch failed: ${url}`, err);
      return null;
    }
  };*/
  const fetchJSON = async (url) => {
    try {
      const realm =
        sessionStorage.getItem("active_realm") ||
        new URLSearchParams(window.location.search).get("realm") ||
        "Idenza";
  
      const response = await fetch(url, {
        headers: { "X-Tenant-Realm": realm },
      });
  
      return await response.json();
    } catch (err) {
      console.error(`Fetch failed: ${url}`, err);
      return null;
    }
  };
  

  const fetchData = (endpoint) => {
    const url = `${apiBase}/${endpoint}?exclude_dev=${EXCLUDE_DEV}`;
    return fetchJSON(url);
  };
  

  const fetchRiskSummary = () => {
    const url = `${apiBase}/case-risk-summary?exclude_dev=${EXCLUDE_DEV}`;
    return fetchJSON(url);
  };

  const fetch3dsSummary = () => {
    const url = `${apiBase}/3ds-summary?exclude_dev=${EXCLUDE_DEV}`;
    console.log("FETCH 3DS URL:", url);
    return fetchJSON(url);
  };

  const fetchBinDistribution = () => {
    const url = `${apiBase}/bin-distribution?exclude_dev=${EXCLUDE_DEV}`;
    return fetchJSON(url);
  };

  const fetchRuleFrequency = () => {
    const url = `${apiBase}/rule-frequency?exclude_dev=${EXCLUDE_DEV}`;
    return fetchJSON(url);
  };

  const fetchTimingTotalStats = () => {
    const url = `${apiBase}/timing-total-stats?exclude_dev=${EXCLUDE_DEV}`;
    return fetchJSON(url);
  };

  const fetchTimingEvaluateStats = () => {
    const url = `${apiBase}/timing-evaluate-stats?exclude_dev=${EXCLUDE_DEV}`;
    return fetchJSON(url);
  };

  // ------------------------------
  // PROCESSORS
  // ------------------------------

  // risk_level_counts
  const processRiskSummary = (response) => {
    if (!response?.data || !Array.isArray(response.data)) {
      return [];
    }

    return response.data
      .filter((r) => (r.count ?? 0) > 0)
      .map((r) => {
        const raw = (r.label || "").toString().toUpperCase();

        const name =
          raw === "HIGH" || raw === "HIGH_RISK"
            ? "HIGH"
            : raw === "MEDIUM" || raw === "MED" || raw === "MEDIUM_RISK"
            ? "MEDIUM"
            : raw === "LOW" || raw === "LOW_RISK"
            ? "LOW"
            : "UNKNOWN";

        return {
          name,
          value: Number(r.count || 0),
          percent: r.percent != null ? Number(r.percent) : 0,
        };
      });
  };

  const processBinDistribution = (raw) => {
    if (!raw?.data) return [];
    return raw.data.map((row) => ({
      bin: row.bin,
      count: Number(row.count || 0),
    }));
  };

  const processRuleFrequency = (raw) => {
    if (!raw?.data) return [];
    return raw.data.map((row) => ({
      rule: row.rule,
      count: Number(row.count || 0),
    }));
  };

  const processTimingStats = (raw) => {
    if (!raw) return null;

    return {
      mean: Number(raw.mean || 0),
      low: Number(raw.low || 0),
      high: Number(raw.high || 0),
    };
  };

  const process3dsSummary = (response) => {
    if (!response?.data || !Array.isArray(response.data)) {
      return [];
    }

    // Buckets with count + percent
    const buckets = {
      "Force 3DS": { value: 0, percent: 0 },
      "Prefer 3DS": { value: 0, percent: 0 },
      "Bypass 3DS": { value: 0, percent: 0 },
      "BLACKLISTED": { value: 0, percent: 0 },
      "BLOCKED": { value: 0, percent: 0 },
      "Other 3DS": { value: 0, percent: 0 },
    };

    response.data.forEach(({ level, count, percent }) => {
      const raw = (level || "").toString().toUpperCase();
      const value = Number(count) || 0;
      const pct = Number(percent) || 0;

      let key;
      if (raw === "FORCE" || raw === "FORCE_ON") {
        key = "Force 3DS";
      } else if (raw === "ATTEMPT" || raw === "PREFER_ON") {
        key = "Prefer 3DS";
      } else if (raw === "BYPASS" || raw === "BYPASS_ON") {
        key = "Bypass 3DS";
      } else if (raw === "BLOCKED" || raw === "BLOCKED_ON") {
        key = "BLOCKED";
      } else if (raw === "BLACKLISTED" || raw === "BLACKLISTED_ON") {
        key = "BLACKLISTED";
      } else {
        key = "Other 3DS";
      }

      buckets[key].value += value;
      buckets[key].percent += pct;
    });

    return Object.entries(buckets)
      .filter(([, v]) => v.value > 0)
      .map(([name, v]) => ({
        name,
        value: v.value,
        percent: Number(v.percent.toFixed(2)),
      }));
  };


  const formatMonthUTC = (d) =>
    new Date(d).toLocaleString("en-US", {
      month: "short",
      year: "numeric",
      timeZone: "UTC", // ✅ critical
    });
  
  const processVampData = (apiResponse) => {
    const rows = apiResponse?.data?.data;
    if (!Array.isArray(rows) || rows.length === 0) return null;
  
    // ✅ For MTD dashboards, ALWAYS use the most recent row (rows[0])
    // (your earlier latestValid logic will intentionally fall back to Dec if Jan has nulls)
    const latest = rows[0];
  
    const monthlySeries = rows
      .slice()
      .reverse()
      .map((row) => ({
        month: formatMonthUTC(row[1]),                 // ✅ fixed
        transactions: Number(row[2] || 0),
        refunds: row[3] == null ? null : Number(row[3] || 0),
        refundRate: row[4] == null ? null : Number(row[4] || 0) * 100,
      }));
  
    return {
      month: formatMonthUTC(latest[1]),               // ✅ fixed
      denom_txn_count: latest[2] || 0,
      refund_count: latest[3] || 0,
      refund_rate: Number(latest[4] || 0) * 100,
      chartData: monthlySeries,
    };
  };
  
  


  const processOrdersData = (apiResponse) => {
    if (!apiResponse?.data) return null;

    return {
      currencies: apiResponse.data,
    };
  };


  const processLatencyData = (data) => {
    if (!data?.data?.data) return null;

    const rows = data.data.data;

    // row[4] is in MINUTES from your analytics query
    const latenciesMinutes = rows.map((row) => parseFloat(row[4] || 0));

    const stats = {
      average: latenciesMinutes.reduce((a, b) => a + b, 0) / latenciesMinutes.length,
      maximum: Math.max(...latenciesMinutes),
      minimum: Math.min(...latenciesMinutes),
    };

    const chartData = rows.slice(0, 15).map((row, index) => {
      const minutes = parseFloat(row[4] || 0);
      const ms = minutes * 60_000;
      return {
        index,
        order_key: row[1].slice(-8),
        valueMs: ms,
      };
    });

    return { stats, chartData };
  };

  // ------------------------------
  // LOAD ALL DATA
  // ------------------------------

  const manualRefresh = async () => {
    console.log("🔄 Manual refresh (read-only)");
    await loadAllData();
  };

  const loadAllData = async () => {
    setLoading(true);

    const [vamp, orders, latency, riskSummary, threeDS, binDist, ruleFreq, timingTotal, timingEval] = await Promise.all(
      [
        fetchData("vamp"),
        fetchData("orders"),
        fetchData("latency"),
        fetchRiskSummary(),
        fetch3dsSummary(),
        fetchBinDistribution(),
        fetchRuleFrequency(),
        fetchTimingTotalStats(),
        fetchTimingEvaluateStats(),
      ]
    );

    setVampData(processVampData(vamp));
    setOrdersData(processOrdersData(orders));
    setLatencyData(processLatencyData(latency));
    setRiskSummaryData(processRiskSummary(riskSummary) || []);
    setThreeDSData(process3dsSummary(threeDS) || []);

    setBinData(processBinDistribution(binDist));
    setRuleData(processRuleFrequency(ruleFreq));

    setTimingTotalStats(processTimingStats(timingTotal));
    setTimingEvaluateStats(processTimingStats(timingEval));

    setLastUpdated(new Date());
    setLoading(false);
  };

  // ------------------------------
  // EFFECT HOOKS
  // ------------------------------
  useEffect(() => {
    loadAllData();
  }, []);

  // useEffect(() => {
  //   if (!autoRefresh) return;

  //   let isRunning = false;

  //   const runRefresh = async () => {
  //     if (isRunning) return; // prevent double triggers
  //     isRunning = true;

  //     console.log("🔥 Auto refresh triggered");

  //     try {
  //       console.log("📡 Calling refresh MV endpoint...");
  //       await refreshMaterializedView(); // refresh the MV
  //       await loadAllData(); // reload dashboard
  //       console.log("✅ Auto refresh complete");
  //     } catch (err) {
  //       console.error("❌ Auto refresh error:", err);
  //     }

  //     isRunning = false;
  //   };

  //   // Run once immediately when toggled ON
  //   runRefresh();

  //   const interval = setInterval(runRefresh, REFRESH_INTERVAL);

  //   return () => clearInterval(interval);
  // }, [autoRefresh, REFRESH_INTERVAL]);

  const SpinnerWrapper = () => (
    <div className="text-center py-4">
      <Spinner animation="border" variant="primary" />
    </div>
  );

  // ------------------------------
  // DERIVED KPI VALUES
  // ------------------------------
  let highRiskRate = "-";
  if (riskSummaryData && riskSummaryData.length > 0) {
    const total = riskSummaryData.reduce((sum, r) => sum + (r.value || 0), 0);
    const high =
      riskSummaryData.find((r) => r.name === "HIGH")?.value ||
      riskSummaryData.find((r) => r.name === "HIGH_RISK")?.value ||
      0;
    if (total > 0) {
      highRiskRate = `${((high / total) * 100).toFixed(1)}%`;
    }
  }

 // const refundRate = vampData && Number.isFinite(vampData.refund_rate) ? `${vampData.refund_rate.toFixed(1)}%` : "-";
 const refundRate =
  vampData && Number.isFinite(vampData.refund_rate)
    ? `${vampData.refund_rate.toFixed(vampData.refund_rate < 0.1 ? 2 : 1)}%`
    : "-";


  // ---- VAMP KPI labeling (demo-grade) ----
const vampMonthLabel = vampData?.month || "—";
const vampTxns = Number(vampData?.denom_txn_count || 0);

const vampRefundsRaw = vampData?.refund_count; // could be null
const hasRefundCount = vampRefundsRaw != null;
const vampRefunds = Number(vampRefundsRaw || 0);

const isNoRefundsYet = vampTxns > 0 && hasRefundCount && vampRefunds === 0;

const refundSublabel = vampData
  ? `${vampMonthLabel} • Month-to-date${isNoRefundsYet ? " • No refunds yet" : ""}`
  : "From VAMP analytics";


  // Force 3DS Rate: FORCE_ON / all 3DS policy/outcome rows
  // Force 3DS Rate: "Force 3DS" / all 3DS policy/outcome rows
  let force3dsRate = "-";
  if (threeDSData && threeDSData.length > 0) {
    const total = threeDSData.reduce((sum, d) => sum + (d.value || 0), 0);
    const force = threeDSData.filter((d) => d.name === "Force 3DS").reduce((sum, d) => sum + (d.value || 0), 0);

    if (total > 0) {
      force3dsRate = `${((force / total) * 100).toFixed(1)}%`;
    }
  }

  const avgLatency =
    latencyData && latencyData.stats && Number.isFinite(latencyData.stats.average)
      ? formatLatencyFromMinutes(latencyData.stats.average)
      : "-";

  const totalVolume = ordersData && ordersData.totals ? `${ordersData.totals.deposited.toFixed(2)}` : "-";

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#F3F4F6",
        padding: "24px",
        fontFamily: "Arial, sans-serif",
      }}
    >
      {/* ===== Top Header ===== */}
      {/* ===== Top Header ===== */}
      {/* ===== Top Header ===== */}
      <header
        style={{
          background: "linear-gradient(90deg, #3B82F6, #60A5FA)", // clean bright blue
          padding: "16px 24px",
          color: "#FFF",
          marginBottom: "16px",
          borderRadius: "10px",
          boxShadow: "0 3px 12px rgba(0,0,0,0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "18px",
        }}
      >
        {/* Left: Logo + Title + Tagline */}
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {/* Idenza logo */}
            <img
              src="/assets/idenza_logo.png"
              alt="Idenza Logo"
              style={{
                width: 40,
                height: 40,
                objectFit: "contain",
                borderRadius: "6px",
              }}
            />

            <h2
              style={{
                margin: 0,
                fontSize: 21,
                fontWeight: 700,
                letterSpacing: "0.01em",
              }}
            >
              Idenza Risk &amp; Payments Command Center
            </h2>
          </div>

          {/* Subheading */}
          <p
            style={{
              margin: "6px 0 0 52px",
              fontSize: 13,
              opacity: 0.92,
              fontWeight: 400,
            }}
          >
            Unified intelligence across approvals, fraud risk, 3DS policy, and VAMP-aligned performance.
          </p>
        </div>

        {/* Right: Tenant + status */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 6,
            fontSize: 12,
          }}
        >
          {/* Tenant / subscriber pill */}
          <div
            style={{
              padding: "5px 12px",
              borderRadius: "999px",
              backgroundColor: "rgba(255,255,255,0.14)",
              border: "1px solid rgba(255,255,255,0.25)",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "999px",
                backgroundColor: "#22C55E", // green indicator
              }}
            />
            <span style={{ fontWeight: 500, color: "#F8FAFC" }}>{subscriberLabel}</span>
          </div>

          {/* Last updated + auto-refresh */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              color: "#E0E7FF",
            }}
          >
            <span>
              Last updated: <strong>{lastUpdated ? lastUpdated.toLocaleTimeString() : "Loading…"}</strong>
            </span>

            <span
              style={{
                padding: "2px 9px",
                borderRadius: "999px",
                backgroundColor: autoRefresh ? "rgba(34,197,94,0.20)" : "rgba(148,163,184,0.25)",
                border: `1px solid ${autoRefresh ? "rgba(34,197,94,0.7)" : "rgba(148,163,184,0.6)"}`,
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              Auto-refresh: <span style={{ fontWeight: 700 }}>{autoRefresh ? "ON" : "OFF"}</span>
            </span>
          </div>
        </div>
      </header>

      <Container fluid>
        {/* Controls row */}
        <Row className="mb-3">
          <Col className="d-flex justify-content-between align-items-center flex-wrap gap-3" style={{ rowGap: "8px" }}>
            <div style={{ fontSize: 12, color: "#4B5563" }}>
              {lastUpdated ? `Last updated: ${lastUpdated.toLocaleTimeString()}` : "Loading..."}
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="btn-group" role="group" aria-label="Time range">
                {["24h", "7d", "30d"].map((range) => (
                  <Button
                    key={range}
                    variant={timeRange === range ? "primary" : "outline-primary"}
                    size="sm"
                    onClick={() => setTimeRange(range)}
                  >
                    Last {range}
                  </Button>
                ))}
              </div>

              <div className="btn-group" role="group" aria-label="Environment">
                {["All", "Prod", "Sandbox"].map((env) => (
                  <Button
                    key={env}
                    variant={environment === env ? "primary" : "outline-primary"}
                    size="sm"
                    onClick={() => setEnvironment(env)}
                  >
                    {env}
                  </Button>
                ))}
              </div>

              <Button
                variant={autoRefresh ? "primary" : "outline-primary"}
                size="sm"
                onClick={() => setAutoRefresh(!autoRefresh)}
              >
                Auto-refresh: {autoRefresh ? "ON" : "OFF"}
              </Button>

              <Button variant="primary" size="sm" onClick={manualRefresh}>
                <FaSyncAlt size={14} />
              </Button>
            </div>
          </Col>
        </Row>

        {/* KPI Strip */}
        {/* ===== KPI Strip ===== */}
        <Row className="mb-4" style={{ gap: "14px 0" }}>
          <Col lg={3} md={6} sm={6}>
            <KpiCard
              label="High Risk Rate"
              value={highRiskRate}
              sublabel="Traffic classified as HIGH"
              icon="⚠️"
              accent="#EF4444"
            />
          </Col>

          <Col lg={3} md={6} sm={6}>
                <KpiCard
                  label="Refund / Dispute Rate"
                  value={refundRate}
                  sublabel={refundSublabel}
                  icon="💸"
                  accent="#F97316"
                />
              </Col>


          <Col lg={3} md={6} sm={6}>
            <KpiCard
              label="Force 3DS Rate"
              value={force3dsRate}
              sublabel="Policy: FORCE_ON"
              icon="🔐"
              accent="#3B82F6"
            />
          </Col>

          <Col lg={3} md={6} sm={6}>
            <KpiCard
              label="Scoring → Deposit"
              value={avgLatency}
              sublabel="Mean scoring-to-deposit latency"
              icon="⏱️"
              accent="#10B981"
            />
          </Col>
        </Row>

        {/* SECOND ROW OF KPIs */}
        <Row className="mb-4" style={{ gap: "14px 0" }}>
          <Col lg={3} md={6} sm={6}>
            <KpiCard
              label="Scoring Time"
              value={timingTotalStats ? `${timingTotalStats.mean.toFixed(2)} ms` : "-"}
              sublabel="Average scoring evaluation time"
              icon="🧠"
              accent="#8B5CF6"
            />
          </Col>
        </Row>

        {/* MAIN GRID LAYOUT */}
        <Row>
          <Col>
            <Card className="shadow-sm border-0" style={{ borderRadius: 12, transition: "box-shadow 0.2s" }}>
              <Card.Body>
                {/* FIRST: RISK SUMMARY + 3DS */}
                <Row className="mb-4">
                  <Col lg={6}>
                    <h6 style={{ fontWeight: 600, marginBottom: 8 }}>Risk Mix</h6>
                    <p
                      style={{
                        fontSize: 12,
                        color: "#6B7280",
                        marginTop: -4,
                        marginBottom: 8,
                      }}
                    >
                      Distribution of Low / Medium / High risk decisions
                    </p>
                    <RiskSummaryAnalytics data={riskSummaryData} loading={loading} />
                  </Col>
                  <Col lg={6}>
                    <h6 style={{ fontWeight: 600, marginBottom: 8 }}>3DS Outcomes &amp; Policy Levels</h6>
                    <p
                      style={{
                        fontSize: 12,
                        color: "#6B7280",
                        marginTop: -4,
                        marginBottom: 8,
                      }}
                    >
                      Mix of actual 3DS gateway results (e.g. ATTEMPT / BYPASS) and Idenza&apos;s Force / Prefer /
                      Bypass 3DS policy recommendations based on risk and rule hits.
                    </p>
                    <ThreeDSAnalytics data={threeDSData} loading={loading} />
                  </Col>
                </Row>

                {/* BIN Distribution + Rule Frequency */}
                <Row className="mb-4">
                  <Col lg={6}>
                    <h6 style={{ fontWeight: 600, marginBottom: 8 }}>BIN Distribution</h6>
                    <p
                      style={{
                        fontSize: 12,
                        color: "#6B7280",
                        marginTop: -4,
                        marginBottom: 8,
                      }}
                    >
                      Top most frequently used card BINs
                    </p>

                    <BinDistribution data={binData} loading={loading} />
                  </Col>

                  <Col lg={6}>
                    <h6 style={{ fontWeight: 600, marginBottom: 8 }}>Rule Hits Frequency</h6>
                    <p
                      style={{
                        fontSize: 12,
                        color: "#6B7280",
                        marginTop: -4,
                        marginBottom: 8,
                      }}
                    >
                      Most frequently triggered fraud rules
                    </p>

                    <RuleFrequency data={ruleData} loading={loading} />
                  </Col>
                </Row>

                {/* SECOND: VAMP + LATENCY */}
                <Row className="mb-4">
                  <Col lg={7}>
                    <h6 style={{ fontWeight: 600, marginBottom: 8 }}>VAMP & Refunds</h6>
                    <p
                      style={{
                        fontSize: 12,
                        color: "#6B7280",
                        marginTop: -4,
                        marginBottom: 8,
                      }}
                    >
                      Transaction vs refund counts and refund rate
                    </p>
                    {loading ? <SpinnerWrapper /> : <VampAnalytics data={vampData} loading={loading} />}
                  </Col>
                  <Col lg={5}>
                    <h6 style={{ fontWeight: 600, marginBottom: 8 }}>Scoring - Deposit Latency</h6>
                    <p
                      style={{
                        fontSize: 12,
                        color: "#6B7280",
                        marginTop: -4,
                        marginBottom: 8,
                      }}
                    >
                      Time between the first SCORING event and the first DEPOSIT for each order
                    </p>
                    {loading ? <SpinnerWrapper /> : <LatencyAnalytics data={latencyData} loading={loading} />}
                  </Col>
                </Row>

                {/* THIRD: ORDERS / VOLUME */}
                <Row>
                  <Col lg={12}>
                    <h6 style={{ fontWeight: 600, marginBottom: 8 }}>Volume & Orders</h6>
                    <p
                      style={{
                        fontSize: 12,
                        color: "#6B7280",
                        marginTop: -4,
                        marginBottom: 8,
                      }}
                    >
                      Deposits, refunds, and withdrawals across orders
                    </p>
                    {loading ? <SpinnerWrapper /> : <OrdersAnalytics data={ordersData} loading={loading} />}
                    <div
                      style={{
                        fontSize: 12,
                        color: "#4B5563",
                        marginTop: 8,
                        textAlign: "right",
                      }}
                    >
                      Total deposited volume: <strong>{totalVolume}</strong>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* ===== Footer ===== */}
      <div
        style={{
          backgroundColor: "#007BFF",
          color: "#FFF",
          textAlign: "center",
          padding: "16px",
          marginTop: "20px",
          borderRadius: "6px",
        }}
      >
        <p style={{ margin: 0 }}>Idenza © 2025</p>
      </div>
    </div>
  );
}
